import React from 'react';
import { Formik, Form, Field as FormikField } from 'formik';
import * as Yup from 'yup';

import { AuthShell } from '../../design-system/components/shells';
import {
  AuthBanner,
  AuthField,
  AuthFields,
  AuthFootnote,
  AuthHint,
  AuthInput,
  AuthSubmit,
} from '../../design-system/patterns/auth';

/**
 * Forgot password, the view. Pure: every input is a prop, every output is a
 * callback. `ForgetPassword.jsx` owns the request, the toasts and the routing.
 *
 * ONE THING AT A TIME, which is the template's rule for this screen: the
 * default state asks for exactly one thing and offers exactly one command. No
 * second path competes with it.
 *
 * DELIBERATE DIFFERENCES FROM THE UPSTREAM TEMPLATE
 * -------------------------------------------------
 * 1. The template's hint under the form says "Signed up with Google? Use
 *    Continue with Google instead". There is no Google identity provider in
 *    this app, so the sentence would be nonsense. Dropped.
 *
 * 2. The template's sent state offers "Resend link" behind a 42 second
 *    cooldown. The app has no resend on this screen, and adding one would be
 *    adding a request the conversion is not allowed to add. The sent state
 *    therefore carries the confirmation and the way back, and nothing else.
 *
 * 3. The template asserts the link "expires in 30 minutes". The app never
 *    states a lifetime and the backend's window is not visible from here, so
 *    the copy does not claim a number.
 */

export const forgetPasswordValidationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
});

export function ForgetPasswordView({
  onSubmit,
  onBackToLogin,
  loading = false,
  sent = false,
  sentEmail = '',
  initialValues = { email: '' },
  initialErrors,
  initialTouched,
}) {
  const footer = (
    <AuthFootnote>
      Remember your password?{' '}
      <button type="button" onClick={onBackToLogin} disabled={loading}>
        Sign in
      </button>
    </AuthFootnote>
  );

  if (sent) {
    return (
      <AuthShell
        title="Check your email"
        subtitle={`We sent a reset link to ${sentEmail}.`}
        back={{ onClick: onBackToLogin, label: 'Back to sign in' }}
        footer={footer}
      >
        <AuthBanner tone="success">
          Open the link in that email to set a new password. It works once.
        </AuthBanner>

        <AuthSubmit type="button" onClick={onBackToLogin}>
          Back to sign in
        </AuthSubmit>

        <AuthHint>
          Nothing in your inbox after a minute? Check your spam folder, and make sure the address
          above is the one on your Sleevr account.
        </AuthHint>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter the email address on your Sleevr account and we will send you a link to set a new password."
      back={{ onClick: onBackToLogin, label: 'Back to sign in' }}
      footer={footer}
    >
      <Formik
        initialValues={initialValues}
        initialErrors={initialErrors}
        initialTouched={initialTouched}
        validationSchema={forgetPasswordValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched }) => {
          const busy = isSubmitting || loading;
          const emailError = touched.email && errors.email ? errors.email : undefined;

          return (
            <Form noValidate>
              <AuthFields>
                <AuthField label="Email address" error={emailError}>
                  <FormikField
                    as={AuthInput}
                    name="email"
                    type="email"
                    placeholder="you@example.co.za"
                    autoComplete="email"
                    disabled={busy}
                  />
                </AuthField>
              </AuthFields>

              <AuthSubmit loading={busy} loadingLabel="Sending reset link">
                Send reset link
              </AuthSubmit>
            </Form>
          );
        }}
      </Formik>
    </AuthShell>
  );
}

export default ForgetPasswordView;
